import { useState, useEffect } from 'react';
import { audit as auditApi, conductores as conductoresApi } from '../services/api';
import {
    Shield, Calendar, Bell, FileText, Users, AlertTriangle, Search,
    ChevronDown, ChevronLeft, ChevronRight, Loader2, X, Filter,
    Clock, User, MessageSquare, Tag, Trash2, ArrowLeftRight
} from 'lucide-react';

const TABS = [
    { id: 'overview', label: 'Vista General', icon: Shield },
    { id: 'calendar', label: 'Cambios Calendario', icon: Calendar },
    { id: 'intercambios', label: 'Intercambios', icon: ArrowLeftRight },
    { id: 'avisos', label: 'Avisos', icon: Bell },
];

const AuditAdmin = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Data states
    const [stats, setStats] = useState(null);
    const [calendarChanges, setCalendarChanges] = useState({ changes: [], pagination: {} });
    const [avisosData, setAvisosData] = useState({ avisos: [], pagination: {} });
    const [intercambiosData, setIntercambiosData] = useState({ intercambios: [], pagination: {} });
    const [users, setUsers] = useState([]);
    const [conductoresList, setConductoresList] = useState([]);

    // Delete modal state
    const [deleteModal, setDeleteModal] = useState({ open: false, change: null });
    const [deleteIntercambioModal, setDeleteIntercambioModal] = useState({ open: false, intercambio: null });
    const [deleting, setDeleting] = useState(false);

    // Filter states
    const [calendarFilters, setCalendarFilters] = useState({
        page: 1,
        limit: 25,
        conductor_id: '',
        created_by: '',
    });

    const [avisosFilters, setAvisosFilters] = useState({
        page: 1,
        limit: 25,
        estado: '',
        created_by: '',
    });

    const [intercambiosFilters, setIntercambiosFilters] = useState({
        page: 1,
        limit: 25,
        conductor_id: '',
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    useEffect(() => {
        if (activeTab === 'calendar') {
            fetchCalendarChanges();
        } else if (activeTab === 'avisos') {
            fetchAvisos();
        } else if (activeTab === 'intercambios') {
            fetchIntercambios();
        }
    }, [activeTab, calendarFilters, avisosFilters, intercambiosFilters]);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [statsData, usersData, conductoresData] = await Promise.all([
                auditApi.getStats(),
                auditApi.getUsers(),
                conductoresApi.getAll(),
            ]);
            setStats(statsData);
            setUsers(usersData.users || []);
            setConductoresList(conductoresData || []);
        } catch (err) {
            console.error('Error fetching audit data:', err);
            setError('Error al cargar datos de auditoría');
        } finally {
            setLoading(false);
        }
    };

    const fetchCalendarChanges = async () => {
        try {
            const data = await auditApi.getCalendarChanges(calendarFilters);
            setCalendarChanges(data);
        } catch (err) {
            console.error('Error fetching calendar changes:', err);
        }
    };

    const fetchAvisos = async () => {
        try {
            const data = await auditApi.getAvisos(avisosFilters);
            setAvisosData(data);
        } catch (err) {
            console.error('Error fetching avisos:', err);
        }
    };

    const fetchIntercambios = async () => {
        try {
            const data = await auditApi.getIntercambios(intercambiosFilters);
            setIntercambiosData(data);
        } catch (err) {
            console.error('Error fetching intercambios:', err);
        }
    };

    const handleDeleteChange = async () => {
        if (!deleteModal.change) return;

        try {
            setDeleting(true);
            await auditApi.deleteCalendarChange(deleteModal.change.id);
            setDeleteModal({ open: false, change: null });
            // Refresh the list
            fetchCalendarChanges();
        } catch (err) {
            console.error('Error deleting change:', err);
            setError(err.message || 'Error al eliminar el cambio');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteIntercambio = async () => {
        if (!deleteIntercambioModal.intercambio) return;

        try {
            setDeleting(true);
            await auditApi.deleteIntercambio(deleteIntercambioModal.intercambio.id);
            setDeleteIntercambioModal({ open: false, intercambio: null });
            // Refresh the list
            fetchIntercambios();
        } catch (err) {
            console.error('Error deleting intercambio:', err);
            setError(err.message || 'Error al eliminar el intercambio');
        } finally {
            setDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateShort = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Overview Tab
    const renderOverview = () => {
        if (!stats) return null;

        return (
            <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Calendar Changes */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-cyan-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.calendarChanges?.total || 0}</p>
                                <p className="text-sm text-gray-400">Cambios Calendario</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Huérfanos:</span>
                            <span className={`font-medium ${stats.calendarChanges?.orphaned > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                {stats.calendarChanges?.orphaned || 0}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                            <span className="text-gray-500">Últimos 7 días:</span>
                            <span className="text-cyan-400 font-medium">{stats.calendarChanges?.last7Days || 0}</span>
                        </div>
                    </div>

                    {/* Avisos */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                <Bell className="w-5 h-5 text-purple-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.avisos?.total || 0}</p>
                                <p className="text-sm text-gray-400">Avisos Totales</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Huérfanos:</span>
                            <span className={`font-medium ${stats.avisos?.orphaned > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                {stats.avisos?.orphaned || 0}
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm mt-1">
                            <span className="text-gray-500">Activos:</span>
                            <span className="text-green-400 font-medium">{stats.avisos?.active || 0}</span>
                        </div>
                    </div>

                    {/* Audit Logs */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.auditLogs?.total || 0}</p>
                                <p className="text-sm text-gray-400">Logs de Auditoría</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Huérfanos:</span>
                            <span className={`font-medium ${stats.auditLogs?.orphaned > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                {stats.auditLogs?.orphaned || 0}
                            </span>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                                <Users className="w-5 h-5 text-green-400" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-white">{stats.notifications?.total || 0}</p>
                                <p className="text-sm text-gray-400">Notificaciones Push</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Huérfanos:</span>
                            <span className={`font-medium ${stats.notifications?.orphaned > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                                {stats.notifications?.orphaned || 0}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Info Card */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                        <Shield className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-lg font-semibold text-white mb-2">¿Qué son los registros huérfanos?</h3>
                            <p className="text-gray-300 text-sm leading-relaxed">
                                Los registros huérfanos son aquellos que fueron creados por usuarios que ya han sido eliminados
                                del sistema. Estos registros se mantienen por motivos de auditoría y trazabilidad, pero ya no
                                tienen un usuario asociado. En las vistas de detalle, estos registros muestran "Usuario eliminado"
                                como creador.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Calendar Changes Tab
    const renderCalendarChanges = () => {
        const { changes, pagination } = calendarChanges;

        return (
            <div className="space-y-4">
                {/* Filters */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex flex-wrap gap-4">
                        {/* Conductor filter */}
                        <div className="relative min-w-[200px]">
                            <select
                                value={calendarFilters.conductor_id}
                                onChange={(e) => setCalendarFilters(prev => ({ ...prev, conductor_id: e.target.value, page: 1 }))}
                                className="w-full appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500"
                            >
                                <option value="">Todos los conductores</option>
                                {conductoresList.map(c => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* User filter */}
                        <div className="relative min-w-[200px]">
                            <select
                                value={calendarFilters.created_by}
                                onChange={(e) => setCalendarFilters(prev => ({ ...prev, created_by: e.target.value, page: 1 }))}
                                className="w-full appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500"
                            >
                                <option value="">Todos los usuarios</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.nombre || u.email}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Results */}
                {changes.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
                        <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No hay cambios de calendario registrados</p>
                    </div>
                ) : (
                    <>
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Fecha</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Conductor</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Motivo/Cambio</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Creado por</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Creado</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-400">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {changes.map(change => (
                                        <tr key={change.id} className="border-b border-white/5 hover:bg-white/5">
                                            <td className="px-4 py-3">
                                                <span className="text-white font-medium">{formatDateShort(change.fecha)}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-gray-300">{change.conductor_nombre || '-'}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {change.motivo_nombre && (
                                                        <span
                                                            className="px-2 py-1 rounded text-xs font-medium"
                                                            style={{
                                                                backgroundColor: `${change.motivo_color}20`,
                                                                color: change.motivo_color
                                                            }}
                                                        >
                                                            {change.motivo_nombre}
                                                        </span>
                                                    )}
                                                    {change.turno_override && (
                                                        <span className="px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs">
                                                            {change.turno_override}
                                                        </span>
                                                    )}
                                                    {change.comentario && (
                                                        <span className="text-gray-500 text-xs" title={change.comentario}>
                                                            <MessageSquare className="w-3 h-3" />
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`text-sm ${change.created_by ? 'text-gray-300' : 'text-orange-400 italic'}`}>
                                                    {change.creado_por_nombre}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-gray-500 text-sm">{formatDate(change.created_at)}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => setDeleteModal({ open: true, change })}
                                                    className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                                                    title="Eliminar cambio"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-400">
                                    Mostrando {changes.length} de {pagination.total} registros
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setCalendarFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page <= 1}
                                        className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span className="text-sm text-gray-300">
                                        Página {pagination.page} de {pagination.totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCalendarFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= pagination.totalPages}
                                        className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Avisos Tab
    const renderAvisos = () => {
        const { avisos, pagination } = avisosData;

        const getEstadoStyle = (estado) => {
            switch (estado) {
                case 'activo': return 'bg-green-500/10 text-green-400';
                case 'archivado': return 'bg-orange-500/10 text-orange-400';
                case 'borrador': return 'bg-gray-500/10 text-gray-400';
                default: return 'bg-gray-500/10 text-gray-400';
            }
        };

        return (
            <div className="space-y-4">
                {/* Filters */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex flex-wrap gap-4">
                        {/* Estado filter */}
                        <div className="relative min-w-[180px]">
                            <select
                                value={avisosFilters.estado}
                                onChange={(e) => setAvisosFilters(prev => ({ ...prev, estado: e.target.value, page: 1 }))}
                                className="w-full appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500"
                            >
                                <option value="">Todos los estados</option>
                                <option value="activo">Activos</option>
                                <option value="borrador">Borradores</option>
                                <option value="archivado">Archivados</option>
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {/* User filter */}
                        <div className="relative min-w-[200px]">
                            <select
                                value={avisosFilters.created_by}
                                onChange={(e) => setAvisosFilters(prev => ({ ...prev, created_by: e.target.value, page: 1 }))}
                                className="w-full appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500"
                            >
                                <option value="">Todos los creadores</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>{u.nombre || u.email}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Results */}
                {avisos.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
                        <Bell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No hay avisos registrados</p>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3">
                            {avisos.map(aviso => (
                                <div key={aviso.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <h3 className="text-white font-medium mb-1">{aviso.titulo}</h3>
                                            <p className="text-gray-400 text-sm line-clamp-2">{aviso.mensaje}</p>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getEstadoStyle(aviso.estado)}`}>
                                            {aviso.estado}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-4 mt-3 text-sm">
                                        <span className={`flex items-center gap-1 ${aviso.creado_por ? 'text-gray-400' : 'text-orange-400'}`}>
                                            <User className="w-3 h-3" />
                                            {aviso.creador_nombre}
                                        </span>
                                        <span className="text-gray-500 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(aviso.fecha_creacion)}
                                        </span>
                                        {aviso.prioridad && (
                                            <span className={`px-2 py-0.5 rounded text-xs ${aviso.prioridad === 'urgente' ? 'bg-red-500/10 text-red-400' :
                                                aviso.prioridad === 'importante' ? 'bg-yellow-500/10 text-yellow-400' :
                                                    'bg-blue-500/10 text-blue-400'
                                                }`}>
                                                {aviso.prioridad}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-400">
                                    Mostrando {avisos.length} de {pagination.total} avisos
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setAvisosFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page <= 1}
                                        className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span className="text-sm text-gray-300">
                                        Página {pagination.page} de {pagination.totalPages}
                                    </span>
                                    <button
                                        onClick={() => setAvisosFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= pagination.totalPages}
                                        className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Intercambios Tab
    const renderIntercambios = () => {
        const { intercambios, pagination } = intercambiosData;

        return (
            <div className="space-y-4">
                {/* Filters */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex flex-wrap gap-4">
                        {/* Conductor filter */}
                        <div className="relative min-w-[200px]">
                            <select
                                value={intercambiosFilters.conductor_id}
                                onChange={(e) => setIntercambiosFilters(prev => ({ ...prev, conductor_id: e.target.value, page: 1 }))}
                                className="w-full appearance-none pl-4 pr-10 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white focus:border-cyan-500"
                            >
                                <option value="">Todos los conductores</option>
                                {conductoresList.map(c => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                ))}
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>
                </div>

                {/* Info card */}
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <ArrowLeftRight className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-300">
                            <strong className="text-orange-400">Importante:</strong> Al eliminar un intercambio se borrarán también los cambios de calendario (CF/CT) asociados a ambos conductores para los días involucrados.
                        </p>
                    </div>
                </div>

                {/* Results */}
                {intercambios.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
                        <ArrowLeftRight className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-400">No hay intercambios de días registrados</p>
                    </div>
                ) : (
                    <>
                        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-white/10">
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Conductor A</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-400">⇄</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Conductor B</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Día Libre A</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Día Libre B</th>
                                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">Fecha Intercambio</th>
                                        <th className="text-center px-4 py-3 text-sm font-medium text-gray-400">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {intercambios.map(intercambio => (
                                        <tr key={intercambio.id} className="border-b border-white/5 hover:bg-white/5">
                                            <td className="px-4 py-3">
                                                <span className="text-white font-medium">{intercambio.conductor_origen_nombre || '-'}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <ArrowLeftRight className="w-4 h-4 text-cyan-400 mx-auto" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-white font-medium">{intercambio.conductor_destino_nombre || '-'}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-300">{formatDateShort(intercambio.dia_origen)}</span>
                                                    <span className="px-1.5 py-0.5 bg-purple-500/10 text-purple-400 rounded text-xs">
                                                        {intercambio.turno_origen}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-300">{formatDateShort(intercambio.dia_destino)}</span>
                                                    <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded text-xs">
                                                        {intercambio.turno_destino}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-gray-500 text-sm">{formatDate(intercambio.fecha_intercambio)}</span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={() => setDeleteIntercambioModal({ open: true, intercambio })}
                                                    className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                                                    title="Eliminar intercambio"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-400">
                                    Mostrando {intercambios.length} de {pagination.total} intercambios
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setIntercambiosFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page <= 1}
                                        className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <span className="text-sm text-gray-300">
                                        Página {pagination.page} de {pagination.totalPages}
                                    </span>
                                    <button
                                        onClick={() => setIntercambiosFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page >= pagination.totalPages}
                                        className="p-2 bg-white/5 rounded-lg hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Auditoría</h1>
                        <p className="text-gray-400">Registro de actividad y trazabilidad del sistema</p>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400">{error}</span>
                    <button onClick={() => setError('')} className="ml-auto">
                        <X className="w-4 h-4 text-red-400" />
                    </button>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                {TABS.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === tab.id
                                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-transparent'
                                }`}
                        >
                            <Icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                </div>
            ) : (
                <>
                    {activeTab === 'overview' && renderOverview()}
                    {activeTab === 'calendar' && renderCalendarChanges()}
                    {activeTab === 'intercambios' && renderIntercambios()}
                    {activeTab === 'avisos' && renderAvisos()}
                </>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.open && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                                <Trash2 className="w-6 h-6 text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">Eliminar Cambio</h3>
                                <p className="text-sm text-gray-400">Esta acción no se puede deshacer</p>
                            </div>
                        </div>

                        {deleteModal.change && (
                            <div className="bg-white/5 rounded-xl p-4 mb-6">
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-gray-500">Fecha:</span>
                                        <span className="text-white ml-2">{formatDateShort(deleteModal.change.fecha)}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">Conductor:</span>
                                        <span className="text-white ml-2">{deleteModal.change.conductor_nombre || '-'}</span>
                                    </div>
                                    {deleteModal.change.motivo_nombre && (
                                        <div className="col-span-2">
                                            <span className="text-gray-500">Motivo:</span>
                                            <span
                                                className="ml-2 px-2 py-0.5 rounded text-xs font-medium"
                                                style={{
                                                    backgroundColor: `${deleteModal.change.motivo_color}20`,
                                                    color: deleteModal.change.motivo_color
                                                }}
                                            >
                                                {deleteModal.change.motivo_nombre}
                                            </span>
                                        </div>
                                    )}
                                    {deleteModal.change.turno_override && (
                                        <div className="col-span-2">
                                            <span className="text-gray-500">Turno:</span>
                                            <span className="ml-2 px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded text-xs">
                                                {deleteModal.change.turno_override}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteModal({ open: false, change: null })}
                                disabled={deleting}
                                className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-xl transition-all disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteChange}
                                disabled={deleting}
                                className="flex-1 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Eliminando...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Eliminar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Intercambio Confirmation Modal */}
            {deleteIntercambioModal.open && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                                <Trash2 className="w-6 h-6 text-red-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-white">Eliminar Intercambio</h3>
                                <p className="text-sm text-gray-400">Se eliminarán también los cambios de calendario</p>
                            </div>
                        </div>

                        {deleteIntercambioModal.intercambio && (
                            <div className="bg-white/5 rounded-xl p-4 mb-6 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400 text-sm">Conductor A:</span>
                                    <span className="text-white font-medium">{deleteIntercambioModal.intercambio.conductor_origen_nombre}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400 text-sm">Conductor B:</span>
                                    <span className="text-white font-medium">{deleteIntercambioModal.intercambio.conductor_destino_nombre}</span>
                                </div>
                                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                                    <span className="text-gray-400 text-sm">Día libre A:</span>
                                    <span className="text-cyan-400">{formatDateShort(deleteIntercambioModal.intercambio.dia_origen)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-400 text-sm">Día libre B:</span>
                                    <span className="text-cyan-400">{formatDateShort(deleteIntercambioModal.intercambio.dia_destino)}</span>
                                </div>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteIntercambioModal({ open: false, intercambio: null })}
                                disabled={deleting}
                                className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-xl transition-all disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDeleteIntercambio}
                                disabled={deleting}
                                className="flex-1 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {deleting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Eliminando...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        Eliminar
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AuditAdmin;
